import type { SelectOption } from "@amtana/design-system";
import type { Catalogs, Translate, Translation } from "@amtana/i18n/catalog";

// Copy for asking to join an organization without an invite (S3.08), and for
// an admin's decision on it. Roles are restated as options here (a contract
// may depend only on zod and @amtana/module, D017), the same shape apikeys'
// own role picker already takes.

export type AccessRequestCopyKey = keyof typeof EN;
export type AccessRequestCatalog = typeof EN;

const EN = {
  roleAdmin: "Admin",
  roleMember: "Member",
  roleViewer: "Viewer",
  joinScreenTitle: "Join an organization · {product}",
  accessRequestsScreenTitle: "Access requests · {product}",
  navAccessRequests: "Access requests",
  breadcrumbHome: "Home",
  breadcrumbSettings: "Settings",
  accessRequestsTitle: "Access requests",
  accessRequestsDescription: "People who asked to join without an invite.",
  approveTitle: "Approve {name}?",
  approveFallbackTitle: "Approve this request?",
  approveDescription: "Choose the role they join with.",
  approve: "Approve",
  role: "Role",
  declineTitle: "Decline {name}?",
  declineFallbackTitle: "Decline this request?",
  declineDescription: "They can ask again later.",
  decline: "Decline",
  requestApproved: "Request approved",
  requestDeclined: "Request declined",
  couldNotApprove: "Could not approve that request",
  couldNotDecline: "Could not decline that request",
  couldNotSendRequest: "Could not send that request",
  requestsTableLabel: "Requests to join",
  noOneAsking: "No one is asking to join",
  columnWho: "Who",
  columnAsked: "Asked",
  columnActions: "Actions",
  approveRequestLabel: "Approve {name}'s request to join",
  declineRequestLabel: "Decline {name}'s request to join",
  missingOrganization: "This link is missing an organization",
  backToSignIn: "Back to sign in",
  joinTitle: "Join an organization",
  joinSignInFirst: "Sign up or sign in first, then open this link again.",
  createAccount: "Create account",
  signIn: "Sign in",
  requestSent: "Request sent",
  adminWillReview: "An admin will review your request.",
  continue: "Continue",
  loading: "Loading…",
  alreadyInTitle: "You're already in {name}",
  alreadyAskedTitle: "You already asked to join {name}",
  waitingForAdmin: "Waiting for an admin to decide.",
  joinNameTitle: "Join {name}",
  asking: "Asking…",
  askToJoin: "Ask to join {name}",
} as const;

const ES: Translation<AccessRequestCatalog> = {
  roleAdmin: "Admin",
  roleMember: "Miembro",
  roleViewer: "Lector",
  joinScreenTitle: "Unirse a una organización · {product}",
  accessRequestsScreenTitle: "Solicitudes de acceso · {product}",
  navAccessRequests: "Solicitudes de acceso",
  breadcrumbHome: "Inicio",
  breadcrumbSettings: "Ajustes",
  accessRequestsTitle: "Solicitudes de acceso",
  accessRequestsDescription: "Personas que pidieron unirse sin una invitación.",
  approveTitle: "¿Aprobar a {name}?",
  approveFallbackTitle: "¿Aprobar esta solicitud?",
  approveDescription: "Elige el rol con el que se unirá.",
  approve: "Aprobar",
  role: "Rol",
  declineTitle: "¿Rechazar a {name}?",
  declineFallbackTitle: "¿Rechazar esta solicitud?",
  declineDescription: "Podrá volver a pedirlo más adelante.",
  decline: "Rechazar",
  requestApproved: "Solicitud aprobada",
  requestDeclined: "Solicitud rechazada",
  couldNotApprove: "No pudimos aprobar esa solicitud",
  couldNotDecline: "No pudimos rechazar esa solicitud",
  couldNotSendRequest: "No pudimos enviar esa solicitud",
  requestsTableLabel: "Solicitudes para unirse",
  noOneAsking: "Nadie ha pedido unirse",
  columnWho: "Quién",
  columnAsked: "Solicitada",
  columnActions: "Acciones",
  approveRequestLabel: "Aprobar la solicitud de {name} para unirse",
  declineRequestLabel: "Rechazar la solicitud de {name} para unirse",
  missingOrganization: "Este enlace no indica una organización",
  backToSignIn: "Volver a iniciar sesión",
  joinTitle: "Unirse a una organización",
  joinSignInFirst: "Crea una cuenta o inicia sesión primero, y luego abre este enlace otra vez.",
  createAccount: "Crear cuenta",
  signIn: "Iniciar sesión",
  requestSent: "Solicitud enviada",
  adminWillReview: "Un administrador revisará tu solicitud.",
  continue: "Continuar",
  loading: "Cargando…",
  alreadyInTitle: "Ya estás en {name}",
  alreadyAskedTitle: "Ya pediste unirte a {name}",
  waitingForAdmin: "Esperando que un administrador decida.",
  joinNameTitle: "Unirse a {name}",
  asking: "Enviando…",
  askToJoin: "Pedir unirme a {name}",
};

export const ACCESS_REQUEST_CATALOGS: Catalogs<AccessRequestCatalog> = { en: EN, es: ES };

/** The three fixed roles, admin first, worded in the active language. */
export function accessRequestRoleOptions(t: Translate<AccessRequestCatalog>): readonly SelectOption[] {
  return [
    { value: "admin", label: t("roleAdmin") },
    { value: "member", label: t("roleMember") },
    { value: "viewer", label: t("roleViewer") },
  ];
}
